import { useState } from 'react';
import { motion } from 'framer-motion';

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqCategory {
  title: string;
  items: FaqItem[];
}

const faqCategories: FaqCategory[] = [
  {
    title: "Product-Related FAQs",
    items: [
      { question: "What types of freezers do you offer?", answer: "Chest freezers, upright freezers, and commercial freezers." },
      { question: "What is the difference between a chest freezer and an upright freezer?", answer: "Chest freezers open from the top and are more energy efficient, while upright freezers are vertical and offer more organization." },
      { question: "What sizes or capacities are available?", answer: "Various sizes — including specifications for gallons, wattage, and volume." },
      { question: "Are your freezers energy efficient?", answer: "Yes, all our models are designed for energy efficiency." },
      { question: "Do your freezers come with a warranty? What does it cover?", answer: "Yes, it covers manufacturer defects and system failures." },
      { question: "What is the expected lifespan of your freezers?", answer: "With proper care, 8-12 years or more." },
      { question: "Do your freezers work in garages or outdoor settings?", answer: "Some models are garage ready. Please confirm the model specifications." },
      { question: "Can I convert the freezer into a chiller?", answer: "Some models may allow it; consult our technician first." }
    ]
  },
  {
    title: "Purchase & Delivery FAQs",
    items: [
      { question: "Where can I buy your freezers — showroom or website?", answer: "You can purchase through both our showroom and official website." },
      { question: "Are you the only distributor?", answer: "Yes, we are the sole authorized distributor of our products." },
      { question: "Do you offer delivery and installation services?", answer: "Yes, we provide both services nationwide." },
      { question: "How long is the delivery time?", answer: "Delivery usually takes 3-7 business days depending on location." },
      { question: "Is there financing or installment payment available?", answer: "Yes, financing is available via partner providers." },
      { question: "Do you accept credit cards?", answer: "Yes, we accept all major payment methods." }
    ]
  },
  {
    title: "Maintenance & Usage FAQs",
    items: [
      { question: "How often should I defrost my freezer?", answer: "No frost system, but if needed, defrost every 3 months." },
      { question: "How do I clean the inside of the freezer safely?", answer: "Unplug, use the included scraper. Avoid sharp objects." },
      { question: "What temperature should I set the freezer to?", answer: "Depends on what you're storing (e.g., meat, ice cream)." },
      { question: "Why is my freezer making noise or clicking sounds?", answer: "These are usually normal. Call us for expert advice if unsure." },
      { question: "What should I do if my freezer isn’t freezing properly?", answer: "Contact us to file a service call report." },
      { question: "Does my unit need an AVR?", answer: "It depends on your location. Consult your technician." }
    ]
  },
  {
    title: "Troubleshooting FAQs",
    items: [
      { question: "Why is my freezer leaking water?", answer: "It may be due to improper drainage or a clogged defrost tube." },
      { question: "What do I do if the power goes out — how long will food stay frozen?", answer: "Do not open the door. It stays cold for 24–48 hrs depending on size." },
      { question: "Why is frost building up inside the freezer?", answer: "Frequent opening or door leakage may cause it." },
      { question: "How do I reset my freezer after a power outage or error?", answer: "Unplug, wait 5 minutes, and plug in again. Observe before restarting." }
    ]
  },
  {
    title: "Warranty & Support FAQs",
    items: [
      { question: "What should I do if my freezer breaks down during the warranty period?", answer: "Report it immediately. Do not tamper with the unit." },
      { question: "How many times can I claim warranty?", answer: "You are allowed one claim under warranty terms." },
      { question: "Where can I find authorized service centers?", answer: "Contact us directly to locate certified technicians near you." }
    ]
  }
];

const filterOptions = ["All", ...faqCategories.map(cat => cat.title)];

export default function FAQsPage() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState("All");

  const handleToggle = (categoryIndex: number, faqIndex: number) => {
    if (activeIndex === faqIndex && activeCategory === categoryIndex) {
      setActiveIndex(null);
      setActiveCategory(null);
    } else {
      setActiveIndex(faqIndex);
      setActiveCategory(categoryIndex);
    }
  };

  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter);
    setActiveIndex(null);
    setActiveCategory(null);
  };

  const filteredCategories = faqCategories.filter(category => {
    if (activeFilter === "All") return true;
    return category.title === activeFilter;
  });

  return (
    <section className="min-h-screen w-full overflow-x-hidden py-20 px-6 md:px-20 relative bg-white dark:bg-[#121212] text-black dark:text-white">
      <div className="absolute inset-0 z-0 bg-white dark:bg-[#121212] bg-[radial-gradient(100%_50%_at_50%_0%,rgba(173,216,230,0.2)_0%,rgba(192,208,224,0.2)_50%,rgba(255,255,255,0)_100%)] dark:bg-[radial-gradient(100%_50%_at_50%_0%,rgba(59,130,246,0.1)_0%,rgba(107,114,128,0.2)_50%,rgba(0,0,0,0)_100%)]" />

      <div className="relative z-10 w-full max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: false }}
          className="text-left mb-12"
        >
          <h2 className="text-5xl font-bold mb-2">FAQs</h2>
          <p className="text-xl text-gray-500 dark:text-gray-300">Find answers to your questions below.</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: false }}
          className="flex flex-wrap gap-2 mb-10 overflow-x-auto scrollbar-hide">
          {filterOptions.map((filter, index) => (
            <button
              key={index}
              onClick={() => handleFilterClick(filter)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                activeFilter === filter
                  ? "bg-blue-700 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              {filter}
            </button>
          ))}
        </motion.div>

        <div className="space-y-6 max-w-6xl mx-auto">
          {filteredCategories.map((category, catIndex) => (
            <div key={catIndex}>
              <motion.h3
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: false }}
                className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2"
              >
                {category.title}
              </motion.h3>
              <div className="grid md:grid-cols-2 gap-6">
                {category.items.map((faq, faqIndex) => {
                  const isOpen = activeIndex === faqIndex && activeCategory === catIndex;

                  return (
                    <motion.div
                      key={`${catIndex}-${faqIndex}`}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: faqIndex * 0.05 }}
                      viewport={{ once: false }}
                      className={`bg-white dark:bg-[#1e1e1e] rounded-xl shadow border border-gray-200 dark:border-gray-700 transition-all duration-300 ${
                        isOpen ? "p-6" : "p-4"
                      }`}
                    >
                      <button
                        className="w-full flex justify-between items-center text-left text-base md:text-lg font-medium text-black dark:text-white"
                        onClick={() => handleToggle(catIndex, faqIndex)}
                      >
                        <span>{faq.question}</span>
                        <span className="text-xl text-gray-500 dark:text-gray-300">
                          {isOpen ? "−" : "+"}
                        </span>
                      </button>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mt-4 text-sm md:text-base text-gray-600 dark:text-gray-300"
                        >
                          {faq.answer}
                        </motion.div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
